import { useState } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
} from "@/components/ui/combobox";
import { useTasks, useDeleteTask } from "@/lib/api/tasks/hooks";
import { useClasses } from "@/lib/api/classes/hooks";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { Plus, Pencil, Trash2, BarChart3, CalendarDays, Upload } from "lucide-react";
import { toast } from "sonner";
import type { Task } from "@/lib/api/tasks/types";

function TasksPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [selectedType, setSelectedType] = useState<"" | "general" | "quiz">("");
  const [searchInput, setSearchInput] = useState("");

  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const { data: classesData } = useClasses();
  const { data: tasksData, isLoading } = useTasks({
    classId: selectedClassId || undefined,
    type: selectedType || undefined,
    page,
    limit: 20,
  });

  const deleteTask = useDeleteTask();

  const handleDelete = (task: Task) => {
    setAlertDialogState({
      title: "태스크 삭제",
      description: `"${task.title}" 태스크를 삭제하시겠습니까?`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteTask.mutateAsync(task._id);
          toast.success("삭제되었습니다.");
        } catch {
          toast.error("삭제에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getTypeLabel = (type: string) => {
    return type === "general" ? "일반" : "퀴즈";
  };

  const getAssigneeLabel = (task: Task) => {
    if (task.assigneeType === "class") {
      return task.class?.name || "반 전체";
    }
    return `${task.students.length}명`;
  };

  // 검색 필터링 (클라이언트 사이드)
  const filteredTasks = tasksData?.tasks.filter((task) => {
    if (!searchInput) return true;
    return task.title.toLowerCase().includes(searchInput.toLowerCase());
  });

  return (
    <PageContainer>
      <Header
        title="태스크 관리"
        right={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate({ to: "/tasks/upload" })}>
              <Upload className="size-4 mr-1" />
              CSV 업로드
            </Button>
            <Button variant="outline" onClick={() => navigate({ to: "/tasks/calendar" })}>
              <CalendarDays className="size-4 mr-1" />
              캘린더
            </Button>
            <Button onClick={() => navigate({ to: "/tasks/new" })}>
              <Plus className="size-4 mr-2" />새 태스크
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        {/* 필터 */}
        <div className="flex flex-wrap gap-3">
          <Combobox
            items={classesData ?? []}
            value={classesData?.find((cls) => cls._id === selectedClassId) ?? null}
            onValueChange={(cls) => {
              setSelectedClassId(cls?._id ?? "");
              setPage(1);
            }}
            itemToStringLabel={(cls) => cls.name}
            isItemEqualToValue={(a, b) => a._id === b._id}
          >
            <ComboboxInput placeholder="전체 반" showClear className="w-[180px]" />
            <ComboboxContent>
              <ComboboxEmpty>결과 없음</ComboboxEmpty>
              <ComboboxList>
                {(cls) => (
                  <ComboboxItem key={cls._id} value={cls}>
                    {cls.name}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>

          <NativeSelect
            value={selectedType || "all"}
            onChange={(e) => {
              setSelectedType(
                e.target.value === "all"
                  ? ""
                  : (e.target.value as "general" | "quiz")
              );
              setPage(1);
            }}
            className="w-[140px]"
          >
            <NativeSelectOption value="all">전체 타입</NativeSelectOption>
            <NativeSelectOption value="general">일반</NativeSelectOption>
            <NativeSelectOption value="quiz">퀴즈</NativeSelectOption>
          </NativeSelect>

          <div className="flex gap-2 flex-1 max-w-sm">
            <Input
              placeholder="제목으로 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1"
            />
          </div>
        </div>

        {/* 테이블 (데스크톱) */}
        <div className="hidden md:block bg-card rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_100px_120px_140px_100px] gap-4 p-4 bg-muted/50 font-medium text-sm">
            <div>제목</div>
            <div>타입</div>
            <div>할당 대상</div>
            <div>기간/날짜</div>
            <div className="text-center">관리</div>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              로딩 중...
            </div>
          ) : filteredTasks?.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              태스크가 없습니다.
            </div>
          ) : (
            filteredTasks?.map((task) => (
              <div
                key={task._id}
                className="grid grid-cols-[1fr_100px_120px_140px_100px] gap-4 p-4 border-t items-center"
              >
                <div className="font-medium truncate">{task.title}</div>
                <div>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      task.type === "quiz"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {getTypeLabel(task.type)}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {getAssigneeLabel(task)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {task.type === "general"
                    ? `${formatDate(task.startDate)} ~ ${formatDate(
                        task.endDate
                      )}`
                    : formatDate(task.date)}
                </div>
                <div className="flex justify-center gap-1">
                  {task.type === "quiz" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate({ to: `/tasks/ranking/${task._id}` })}
                      title="랭킹"
                    >
                      <BarChart3 className="size-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate({ to: `/tasks/${task._id}/edit` })}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(task)}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 카드 (모바일) */}
        <div className="md:hidden space-y-3">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              로딩 중...
            </div>
          ) : filteredTasks?.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              태스크가 없습니다.
            </div>
          ) : (
            filteredTasks?.map((task) => (
              <div key={task._id} className="bg-card rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{task.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {getAssigneeLabel(task)}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      task.type === "quiz"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {getTypeLabel(task.type)}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {task.type === "general"
                    ? `${formatDate(task.startDate)} ~ ${formatDate(
                        task.endDate
                      )}`
                    : formatDate(task.date)}
                </div>
                <div className="flex gap-2">
                  {task.type === "quiz" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate({ to: `/tasks/ranking/${task._id}` })}
                    >
                      <BarChart3 className="size-4 mr-1" />
                      랭킹
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate({ to: `/tasks/${task._id}/edit` })}
                  >
                    <Pencil className="size-4 mr-1" />
                    수정
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(task)}
                  >
                    <Trash2 className="size-4 mr-1 text-destructive" />
                    삭제
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {tasksData && tasksData.totalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              이전
            </Button>
            <span className="flex items-center px-4">
              {page} / {tasksData.totalPages}
            </span>
            <Button
              variant="outline"
              disabled={page === tasksData.totalPages}
              onClick={() => setPage(page + 1)}
            >
              다음
            </Button>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/tasks/")({
  component: TasksPage,
});
